import { API_METHODS } from '@compass/client/constants/api-methods';
import type { UserRole } from '@compass/client/types/membership';
import { programUserService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useStableFold } from '@/hooks/internal/useStableFold';
import { useCanCallAPI, usePermissionMap } from '@/hooks/usePermissions';
import { batchUserIds } from '@/hooks/useTeams';
import { useMyRoles } from '@/hooks/useUsers';
import { isBelowProgramAdmin, isCaseProgramAdmin } from '@/utils/caseActions';

/** SearchProgramUsers returns at most this many memberships per page. */
const SEARCH_PAGE_SIZE = 100;
/** Stops a cursor that never ends. 20 pages is 2,000 memberships for 100 users. */
const MAX_SEARCH_PAGES = 20;

interface ProgramRolesBatch {
  organizationId: string;
  programId: string;
  /** User id → that user's roles on the program. */
  rolesByUser: Record<string, UserRole[]>;
}

/**
 * Every program role of one batch (≤100) of users. The search returns one row per
 * membership and pages by membership id, so it is read until it runs out. It answers 404
 * once no membership is left: on the page after the last one, or straight away when none
 * of the users has a role on the program (how a batch of Organization Admins looks). The
 * dashboard client rejects a 404, so that is the normal end, not a failure.
 */
export async function fetchProgramRoles(
  organizationId: string,
  programId: string,
  userIds: string[],
): Promise<ProgramRolesBatch> {
  const rolesByUser: Record<string, UserRole[]> = {};
  let startAfter: string | undefined;
  for (let page = 0; page < MAX_SEARCH_PAGES; page++) {
    const response = await searchPage(organizationId, programId, userIds, startAfter);
    if (!response) break;
    const users = response.users ?? [];
    for (const user of users) {
      if (!user.user_id) continue;
      rolesByUser[user.user_id] = [...(rolesByUser[user.user_id] ?? []), ...(user.roles ?? [])];
    }
    startAfter = users[users.length - 1]?.membership_id;
    if (!startAfter) break;
  }
  return { organizationId, programId, rolesByUser };
}

/** One page of the search, or null when it answers 404 (no membership left). */
async function searchPage(
  organizationId: string,
  programId: string,
  userIds: string[],
  startAfter: string | undefined,
) {
  try {
    return await programUserService.searchProgramUsers({
      organization_id: organizationId,
      program_id: programId,
      user_ids: userIds,
      limit: SEARCH_PAGE_SIZE,
      start_after_id: startAfter,
    });
  } catch (err) {
    if ((err as { APIHttpStatus?: number })?.APIHttpStatus === 404) return null;
    throw err;
  }
}

/** Per program, the users whose role on it is below Program Admin. Exported for tests. */
export function foldBelowProgramAdmin(
  batches: Array<ProgramRolesBatch | undefined>,
): Map<string, Set<string>> {
  const below = new Map<string, Set<string>>();
  for (const batch of batches) {
    if (!batch) continue;
    const users = below.get(batch.programId) ?? new Set<string>();
    for (const [userId, roles] of Object.entries(batch.rolesByUser)) {
      if (isBelowProgramAdmin(roles, batch.organizationId, batch.programId)) users.add(userId);
    }
    below.set(batch.programId, users);
  }
  return below;
}

interface UseBelowProgramAdminUsersParams {
  organizationId?: string;
  /** Programs of the cases being assigned. */
  programIds: string[];
  /** Candidate assignees. */
  userIds: string[];
  /** Look roles up only when a case's human audit is reserved for Program Admins. */
  enabled: boolean;
}

/**
 * Which candidate assignees to hide from a case whose human audit is reserved for Program
 * Admins and above (BV-2657): `isBelow(programId, userId)` is true for a user whose role
 * on that program is below Program Admin. A user with no role on the program (an
 * Organization Admin) is kept. The viewer is judged by their own full roles instead, so
 * an Organization Admin who also holds a lower program role can still take the case.
 *
 * Fails closed: while the roles load, when the viewer cannot read them, or when the lookup
 * fails, everyone counts as below, so a non-admin is never offered. Every role that can
 * assign cases can read program users, so the closed state only lasts while loading.
 */
export function useBelowProgramAdminUsers({
  organizationId,
  programIds,
  userIds,
  enabled,
}: UseBelowProgramAdminUsersParams) {
  const canSearch = useCanCallAPI(API_METHODS.ProgramUserService.SearchProgramUsers);
  const { isLoading: permissionsLoading } = usePermissionMap();
  const currentUserId = useStore(state => state.user?.id);
  const { data: myRoles } = useMyRoles();

  const programs = useMemo(() => [...new Set(programIds.filter(Boolean))].sort(), [programIds]);
  const batches = useMemo(() => batchUserIds(userIds), [userIds]);
  const orgId = enabled && programs.length > 0 && batches.length > 0 ? organizationId : undefined;
  const active = !!orgId;

  const queries = useQueries({
    queries: orgId && canSearch ? rolesQueries(orgId, programs, batches) : [],
  });

  const below = useStableFold(
    queries,
    q => Object.keys(q.data?.rolesByUser ?? {}).length,
    qs => foldBelowProgramAdmin(qs.map(q => q.data)),
  );

  const { isLoading, blind } = lookupStatus(active, permissionsLoading, canSearch, queries);

  const isBelow = useCallback(
    (programId: string | undefined, userId: string) => {
      if (!active) return false;
      if (userId === currentUserId) {
        return !isCaseProgramAdmin(myRoles?.user_roles, organizationId, programId);
      }
      return blind || !!(programId && below.get(programId)?.has(userId));
    },
    [active, blind, below, currentUserId, myRoles, organizationId],
  );

  return { isBelow, isLoading };
}

/**
 * `blind` = the roles cannot be trusted yet (loading, unreadable, or failed), so everyone
 * counts as below.
 */
function lookupStatus(
  active: boolean,
  permissionsLoading: boolean,
  canSearch: boolean,
  queries: Array<{ isLoading: boolean; isError: boolean }>,
): { isLoading: boolean; blind: boolean } {
  if (!active) return { isLoading: false, blind: false };
  const isLoading = permissionsLoading || queries.some(q => q.isLoading);
  return { isLoading, blind: isLoading || !canSearch || queries.some(q => q.isError) };
}

/** One query per (program, batch of ≤100 users). */
function rolesQueries(organizationId: string, programs: string[], batches: string[][]) {
  return programs.flatMap(programId =>
    batches.map(batch => ({
      queryKey: queryKeys.programUsers.roles(organizationId, programId, batch),
      queryFn: () => fetchProgramRoles(organizationId, programId, batch),
      staleTime: 5 * 60 * 1000,
    })),
  );
}
